import { Form, Flex, Input, Button, ColorPicker, List } from "antd";

import { useMemo, createContext } from "react";

import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import PlusIcon from "@/icons/PlusIcon";
import TrashIcon from "@/icons/TrashIcon";

import DragHandle from "@/components/ui/DragHandle";

const PRESETS_COLORS = [
  "#367AF0",
  "#8C5CC8",
  "#cf1998",
  "#EC4032",
  "#E68525",
  "#ffbf00",
  "#249F5D",
  "#4091AE",
  "#808080",
];

const SortableListItemContext = createContext({});

const TableParametersList = ({ fieldName }) => {
  const SortableListItem = (props) => {
    const { itemKey, style, ...rest } = props;
    const {
      attributes,
      listeners,
      setNodeRef,
      setActivatorNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: itemKey });
    const listStyle = {
      ...style,
      transform: CSS.Translate.toString(transform),
      transition,
      ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
    };
    const memoizedValue = useMemo(
      () => ({ setActivatorNodeRef, listeners, attributes }),
      [setActivatorNodeRef, listeners, attributes],
    );
    return (
      <SortableListItemContext.Provider value={memoizedValue}>
        <List.Item {...rest} ref={setNodeRef} style={listStyle} />
      </SortableListItemContext.Provider>
    );
  };

  return (
    <Form.List
      name={fieldName}
      rules={[
        {
          required: true,
          message: "Добавьте хотя бы один параметр",
        },
      ]}
    >
      {(fields, { add, remove, move }, { errors }) => (
        <>
          {fields.length > 0 && (
            <DndContext
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={({ active, over }) => {
                if (!over || active.id === over.id) return;

                const oldIndex = fields.findIndex((f) => f.key === active.id);
                const newIndex = fields.findIndex((f) => f.key === over.id);
                if (oldIndex === -1 || newIndex === -1) return;

                move(oldIndex, newIndex);
              }}
              id="columns-drag-sorting"
            >
              <SortableContext
                items={fields.map((item) => item.key)}
                strategy={verticalListSortingStrategy}
              >
                <List
                  grid={{ gutter: 10, column: 1 }}
                  dataSource={fields}
                  renderItem={({ key, name: idx, ...restField }) => {
                    return (
                      <SortableListItem key={key} itemKey={key}>
                        <Flex align="start" gap={10} justify="space-between">
                          <Flex align="center" gap={10} style={{ flex: 1 }}>
                            <DragHandle
                              context={SortableListItemContext}
                              style={{ alignSelf: "center" }}
                            />

                            <Form.Item
                              {...restField}
                              name={[idx, "color"]}
                              initialValue={
                                PRESETS_COLORS[idx % PRESETS_COLORS.length]
                              }
                              style={{ marginBottom: 0 }}
                              normalize={(color) => color?.toHexString?.()}
                            >
                              <ColorPicker
                                size="small"
                                disabledAlpha
                                presets={[
                                  {
                                    label: "Цвета",
                                    colors: PRESETS_COLORS,
                                  },
                                ]}
                                panelRender={(
                                  _,
                                  { components: { Picker, Presets } },
                                ) => (
                                  <Flex gap={10}>
                                    <Flex vertical gap={10}>
                                      <Presets />
                                      <Picker />
                                    </Flex>
                                  </Flex>
                                )}
                              />
                            </Form.Item>

                            <Form.Item
                              {...restField}
                              name={[idx, "name"]}
                              rules={[
                                {
                                  required: true,
                                  message: "",
                                },
                              ]}
                              style={{ marginBottom: 0, flex: 1 }}
                            >
                              <Input placeholder="Название" />
                            </Form.Item>
                          </Flex>
                          <Button
                            type="text"
                            icon={<TrashIcon />}
                            onClick={() => remove(idx)}
                          ></Button>
                        </Flex>
                      </SortableListItem>
                    );
                  }}
                ></List>
              </SortableContext>
            </DndContext>
          )}

          <Button
            type="dashed"
            icon={<PlusIcon />}
            aria-label="Добавить параметр"
            onClick={() =>
              add({
                name: "",
                color: PRESETS_COLORS[fields.length % PRESETS_COLORS.length],
              })
            }
          >
            Добавить параметр
          </Button>

          <Form.ErrorList errors={errors} />
        </>
      )}
    </Form.List>
  );
};

export default TableParametersList;
